import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, ShieldAlert, Ghost, Link2Off, XCircle, GitBranch, CheckCircle2 } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Navigate } from "react-router-dom";
import StickyContextBar from "@/components/StickyContextBar";

/**
 * DataIntegrityPage — 149-aq. BOD Lv1 only.
 *
 * Halaman admin yang scan seluruh WO/VO utk deteksi:
 *  - Mirror mismatch (project_name/client/lokasi tidak match parent aktual)
 *  - Missing parent reference (parent_project_id null)
 *  - Orphan (parent_project_id points to hilang)
 *  - Cancelled parent (child active tapi parent cancelled)
 *  - Wrong parent type (parent bukan new_project)
 *
 * Aksi cepat:
 *  - Fix mirror mismatch → panggil `/admin/sync-child-project-names`
 */

const ISSUE_META = {
  mirror_mismatch: {
    icon: GitBranch, color: "amber",
    title: "Mirror Mismatch",
    desc: "WO/VO dgn project_name / parent_project_name / client / lokasi tidak match parent-nya. Bisa auto-fix dgn tombol Sync di bawah.",
  },
  missing_parent_ref: {
    icon: Link2Off, color: "rose",
    title: "Missing Parent Reference",
    desc: "WO/VO tanpa parent_project_id. Perlu edit manual — set parent lewat dialog Edit.",
  },
  orphan: {
    icon: Ghost, color: "rose",
    title: "Orphan Children",
    desc: "WO/VO dgn parent_project_id yg parent-nya HILANG dari database. Perlu edit manual atau delete.",
  },
  cancelled_parent: {
    icon: XCircle, color: "orange",
    title: "Cancelled Parent",
    desc: "WO/VO aktif tapi parent-nya cancelled. Perlu tinjau — cancel juga WO atau pindah parent.",
  },
  wrong_parent_type: {
    icon: ShieldAlert, color: "purple",
    title: "Wrong Parent Type",
    desc: "WO/VO dgn parent bertipe BUKAN new_project (nested illegal). Perlu pindah ke parent new_project.",
  },
};

const COLOR_MAP = {
  amber: "border-amber-300 bg-amber-50 text-amber-800",
  rose: "border-rose-300 bg-rose-50 text-rose-800",
  orange: "border-orange-300 bg-orange-50 text-orange-800",
  purple: "border-purple-300 bg-purple-50 text-purple-800",
  green: "border-emerald-300 bg-emerald-50 text-emerald-800",
};

function IssueCard({ type, items, expanded, onToggle }) {
  const meta = ISSUE_META[type];
  const Icon = meta.icon;
  const color = COLOR_MAP[meta.color];
  const count = items.length;
  return (
    <div className={`border ${color.split(" ")[0]} bg-white`} data-testid={`issue-card-${type}`}>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-4 py-3 border-l-4 ${color.replace("bg-white", "")} ${color} hover:opacity-90 transition-opacity`}
        data-testid={`issue-toggle-${type}`}
      >
        <Icon size={20} />
        <div className="flex-1 text-left">
          <div className="font-semibold text-sm">{meta.title}</div>
          <div className="text-[10px] leading-relaxed opacity-80 mt-0.5">{meta.desc}</div>
        </div>
        <div className="text-2xl font-bold tabular-nums" data-testid={`issue-count-${type}`}>{count}</div>
      </button>
      {expanded && count > 0 && (
        <div className="max-h-96 overflow-y-auto border-t border-slate-200" data-testid={`issue-list-${type}`}>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 ens-sticky-below-topbar" style={{background:'rgb(248 250 252)'}}>
              <tr className="text-left">
                <th className="px-3 py-2 overline text-slate-600 w-12">Type</th>
                <th className="px-3 py-2 overline text-slate-600">WO/VO</th>
                <th className="px-3 py-2 overline text-slate-600">Current Parent Name</th>
                <th className="px-3 py-2 overline text-slate-600">Expected / Detail</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={it.child_id || idx} className="border-b border-slate-100 hover:bg-amber-50/40">
                  <td className="px-3 py-2">
                    <span className={`text-[9px] font-bold px-1 py-0.5 uppercase tracking-wider border ${it.child_type === "working_order" ? "bg-sky-50 text-sky-700 border-sky-300" : "bg-orange-50 text-orange-700 border-orange-300"}`}>
                      {it.child_type === "working_order" ? "WO" : "VO"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] font-semibold text-[#0B1D3A]">{it.label}</td>
                  <td className="px-3 py-2 text-slate-600">{it.current_parent_name || "(kosong)"}</td>
                  <td className="px-3 py-2 text-[10px] text-slate-700">
                    {type === "mirror_mismatch" && it.diff && (
                      <div className="space-y-0.5">
                        <div className="font-semibold text-slate-500">Should be: {it.parent_name}</div>
                        {Object.entries(it.diff).map(([field, val]) => (
                          <div key={field} className="font-mono">
                            <span className="text-rose-700">{field}:</span>{" "}
                            <span className="line-through text-rose-500">{String(val.current || "-")}</span>{" → "}
                            <span className="text-emerald-700">{String(val.expected || "-")}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {type === "missing_parent_ref" && <span className="italic text-slate-500">Butuh edit manual — set parent lewat dialog Edit</span>}
                    {type === "orphan" && <span>Parent ID hilang: <span className="font-mono">{it.parent_id?.slice(0, 12)}…</span></span>}
                    {type === "cancelled_parent" && <span>Parent <b>{it.parent_name}</b> cancelled</span>}
                    {type === "wrong_parent_type" && <span>Parent <b>{it.parent_name}</b> bertipe <b>{it.parent_type}</b> — bukan new_project</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function DataIntegrityPage() {
  const { user } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState({});

  const isBodLv1 = user?.division === "BOD" && Number(user?.level) === 1;

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.get("/admin/data-integrity-scan");
      setReport(data);
      // Auto-expand issue cards yang non-zero
      const auto = {};
      Object.entries(data.summary || {}).forEach(([k, v]) => { if (v > 0) auto[k] = true; });
      setExpanded(auto);
      const totalIssues = Object.values(data.summary || {}).reduce((a, b) => a + b, 0);
      if (totalIssues === 0) {
        toast.success("✅ Data bersih — tidak ada corruption ditemukan.");
      } else {
        toast.warning(`Ditemukan ${totalIssues} isu di ${data.scanned_children} WO/VO. Cek detail di bawah.`);
      }
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Scan gagal");
    } finally {
      setScanning(false);
    }
  };

  const runMirrorSync = async () => {
    if (!window.confirm(
      "Fix Mirror Mismatch:\n\n" +
      "Backfill project_name / parent_project_name / client / city / province utk semua WO/VO yg tidak match parent-nya.\n\nIdempotent — aman dijalankan berkali-kali.\n\nLanjut?"
    )) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync-child-project-names");
      if (data.updated === 0) {
        toast.success("✅ Sudah sinkron — tidak ada yg di-fix.");
      } else {
        toast.success(`✅ ${data.updated} WO/VO di-fix dari ${data.scanned}.`);
      }
      // Re-scan otomatis biar count refresh
      await runScan();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Sync gagal");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { if (isBodLv1) runScan(); /* eslint-disable-next-line */ }, [isBodLv1]);

  if (user && !isBodLv1) {
    return <Navigate to="/dashboard" replace />;
  }

  const totalIssues = report ? Object.values(report.summary || {}).reduce((a, b) => a + b, 0) : 0;
  const clean = report && totalIssues === 0;

  return (
    <div className="p-8 space-y-6" data-testid="data-integrity-page">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="overline text-slate-500 text-[11px]">Administration · BOD</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0B1D3A]">Dasbor Integritas Data</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl leading-relaxed">
            Scan seluruh hierarki project untuk deteksi data corruption:{" "}
            <span className="font-semibold">mirror fields tidak sinkron</span>, orphan children,
            broken parent references, cancelled parent, dan nested illegal.
            Deteksi proaktif sebelum user lapor.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            className="rounded-none border-slate-400 uppercase text-xs tracking-wider font-semibold"
            disabled={scanning}
            onClick={runScan}
            data-testid="btn-rescan"
          >
            <RefreshCw size={13} className={`mr-1.5 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scanning…" : "Rescan"}
          </Button>
          {report && report.summary?.mirror_mismatch > 0 && (
            <Button
              className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold"
              disabled={syncing}
              onClick={runMirrorSync}
              data-testid="btn-fix-mirror"
            >
              {syncing ? "Fixing…" : `Fix ${report.summary.mirror_mismatch} Mirror`}
            </Button>
          )}
        </div>
      </div>

      <StickyContextBar
        segments={["Administrasi", "Integritas Data", report ? `${totalIssues} isu` : "Belum scan"]}
        extra={<span className="text-slate-400 normal-case">{report ? `${report.scanned_children} WO/VO discanned` : ""}</span>}
      />

      {/* Overall summary bar */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="summary-stats">
          <div className="border border-slate-200 bg-white p-3">
            <div className="overline text-slate-500 text-[10px]">Scanned</div>
            <div className="text-2xl font-bold tabular-nums text-[#0B1D3A]" data-testid="stat-scanned">{report.scanned_children}</div>
            <div className="text-[10px] text-slate-500">WO/VO ditelusuri</div>
          </div>
          <div className="border border-slate-200 bg-white p-3">
            <div className="overline text-slate-500 text-[10px]">Parents</div>
            <div className="text-2xl font-bold tabular-nums text-[#0B1D3A]">{report.parents_indexed}</div>
            <div className="text-[10px] text-slate-500">New Project di-index</div>
          </div>
          {Object.entries(report.summary || {}).map(([k, v]) => (
            <div key={k} className={`border p-3 ${v > 0 ? COLOR_MAP[ISSUE_META[k].color] : COLOR_MAP.green}`}>
              <div className="overline text-[10px] opacity-80">{ISSUE_META[k].title}</div>
              <div className="text-2xl font-bold tabular-nums" data-testid={`stat-${k}`}>{v}</div>
              <div className="text-[10px] opacity-70">issue ditemukan</div>
            </div>
          ))}
        </div>
      )}

      {clean && (
        <div className="border border-emerald-300 bg-emerald-50 p-6 flex items-center gap-4" data-testid="clean-banner">
          <CheckCircle2 size={32} className="text-emerald-600 shrink-0" />
          <div>
            <div className="font-semibold text-emerald-800 text-base">Data Bersih 🎉</div>
            <div className="text-xs text-emerald-700 mt-1">
              Semua {report.scanned_children} WO/VO memiliki referensi parent yang valid + mirror fields sinkron.
              Tidak ada data corruption yang terdeteksi.
            </div>
          </div>
        </div>
      )}

      {/* Issue cards */}
      {report && !clean && (
        <div className="space-y-3">
          {Object.keys(ISSUE_META).map((k) => (
            <IssueCard
              key={k}
              type={k}
              items={report[k] || []}
              expanded={!!expanded[k]}
              onToggle={() => setExpanded((prev) => ({ ...prev, [k]: !prev[k] }))}
            />
          ))}
        </div>
      )}

      {!report && !scanning && (
        <div className="border border-dashed border-slate-300 p-8 text-center text-slate-500 text-sm">
          Klik <b>Rescan</b> untuk memulai audit.
        </div>
      )}
    </div>
  );
}
